/**
 * Putting the agent key where it belongs, in one click.
 *
 * In a browser the only way to give an agent its key is a command pasted into a terminal that sets
 * an environment variable — which is the thing people get wrong, and the worst place for a secret
 * that is now in a shell history. The desktop app has the operating system's own credential store,
 * so it offers that instead and the command stays there for whoever wants it.
 *
 * Renders nothing outside the desktop app, so callers need no branch.
 */

import { useState } from 'react'
import { Check, Lock, ShieldAlert } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { isDesktop, vaultStore } from '../lib/desktop'

export function VaultButton({ value }: { value: string }) {
  const { t } = useI18n()
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')

  if (!isDesktop()) return null

  const save = async (): Promise<void> => {
    setState('saving')
    try {
      await vaultStore(value)
      setState('saved')
    } catch {
      setState('failed')
    }
  }

  return (
    <div className="stack stack--tight">
      {state === 'saved' ? (
        <div className="row row--tight small" style={{ color: 'var(--ok-text)' }}>
          <Check size={14} aria-hidden /> {t('vault.saved')}
        </div>
      ) : (
        <button type="button" className="btn btn--sm btn--primary" onClick={() => void save()} disabled={state === 'saving'}>
          <Lock size={13} aria-hidden /> {state === 'saving' ? t('common.saving') : t('vault.save')}
        </button>
      )}

      <span className="tiny muted">{t('vault.hint')}</span>

      {state === 'failed' && (
        <div className="connect__warning small" role="alert">
          <ShieldAlert size={14} aria-hidden /> {t('vault.failed')}
        </div>
      )}
    </div>
  )
}
